<template>
  <div class="relative redemption-report flex flex-col justify-center items-center">
    <div class="w-full">
      <report-header
        :error.sync="error"
        :range="range"
        :report="report"
        @getReport="getReport"
      >
        <template #title>
          redemption report
        </template>
        <template #subtitle>
          analyze how members are redeeming rewards
        </template>
      </report-header>
      <template v-if="report">
        <div class="flex justify-center items-center w-full text-center my-6">
          <div class="w-1/4 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="totalPointsRedeemed || totalPointsRedeemed === 0">
              <div class="text-lg font-primary">total points redeemed</div>
              <div class="text-2xl">{{totalPointsRedeemed}}</div>
            </template>
          </div>
          <div class="w-1/4 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="uniqueMembersRedeemed || uniqueMembersRedeemed === 0">
              <div class="text-lg font-primary">unique members redeemed</div>
              <div class="text-2xl">{{uniqueMembersRedeemed}}</div>
            </template>
          </div>
          <div class="w-1/4 flex-1 md:flex-none">
            <loading-icon v-if="loadingReport" class="mx-auto" />
            <template v-else-if="rewardsRedeemed || rewardsRedeemed === 0">
              <div class="text-lg font-primary">rewards redeemed</div>
              <div class="text-2xl">{{rewardsRedeemed}}</div>
            </template>
          </div>
        </div>
      </template>
    </div>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else-if="report" class="mt-12 w-full">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        :defaultSort="sortDirection"
        @sortChange="updateTableSort"
        disableTableSort
        sortable
      />
      <div v-if="sortLoading" class="flex justify-center items-center m-auto w-full">
        <loading-icon />
      </div>
      <div v-else-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { utcToZonedTime } from "date-fns-tz";
import { DateRange, SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { reportsAPI } from '../../services/api/reportsAPI';
import ReportHeader from "./ReportHeader.vue";
import { mapGetters } from 'vuex';

export default {
  name: "RedemptionReport",
  components: {
    DateRange,
    SbCustomTable,
    Pagination,
    LoadingIcon,
    ReportHeader,
  },
  data() {
    return {
      range: null,
      page: 0,
      sortColumn: 0,
      sortDirection: "desc",
      report: null,
      tableRows: [],
      loadingReport: false,
      sortLoading: false,
      error: {},
      tableColumns: [
        { key: "reward_value", value: "reward value" },
        { key: "reward_name", value: "reward name" },
        { key: "num_of_redemptions", value: "number of \n redemptions" },
        { key: "points_used", value: "points \n used" },
        { key: "percent_of_total", value: "percentage of total \n points redeemed" }
      ],
    }
  },
  async created() {
    await this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.range = this.defaultDateRange();
  },
  computed: {
    pagesTotal(){
      return this.report?.pagination?.total_pages || 1;
    },
    rewardsRedeemed() {
      return this.report?.output_data.rewards_redeemed || 0;
    },
    totalPointsRedeemed() {
      return parseInt(this.report?.output_data.total_points_redeemed) || 0;
    },
    uniqueMembersRedeemed() {
      return this.report?.output_data.unique_members_redeemed || 0;
    },
    ...mapGetters(['merchantTimeZone'])
  },
  watch: {
    page() {
      this.getReport();
    }
  },
  methods: {
    defaultDateRange() {
      const currentDate = utcToZonedTime(new Date(), this.merchantTimeZone);
      const minimumDate = subDays(currentDate, 90);

      return {
        min: minimumDate,
        start: minimumDate,
        max: subDays(currentDate, 1),
        end: subDays(currentDate, 1)
      }
    },
    formatReportData() {
      const results = this.report.output_data.results;

      if (results.length > 0) {
        this.tableRows = results.map(result => ({
          ...result,
          points_used: parseInt(result.points_used),
          percent_of_total: `${parseInt(result.percent_of_total)}%`,
        }));
      }
    },
    async updateTableSort(sort) {
      const index = this.tableColumns.findIndex(column => column.key === sort.column);
      if (
        (this.sortColumn !== index ||
        this.sortDirection !== sort.sorting) &&
        this.tableRows.length > 0
      ) {
        this.tableRows = [];
        this.sortLoading = true;
        this.sortColumn = index;
        this.sortDirection = sort.sorting;
        await this.getReport();
        this.sortLoading = false;
      }
    },
    createNewReport() {
      return reportsAPI.createReport('Reports::RedemptionsReport', this.range.start, this.range.end);
    },
    fetchExistingReport() {
      return reportsAPI.getReportById(this.report.id, this.page + 1, { index: this.sortColumn, direction: this.sortDirection });
    },
    async getReport(newReport) {
      this.loadingReport = true;
      try {
        const { report, pagination } = !this.report?.id || (this.report.id && newReport) ? await this.createNewReport()
                                                                                         : await this.fetchExistingReport();
        this.report = { pagination, ...report };
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.redemption-report {
  .sb-table__head-item {
    white-space: pre-line!important;
    margin-left: 4px;
    .top-0 {
      top: 50%!important;
      transform: translateY(-50%);
    }
    .sb-table__head-item__arrow {
      line-height: 6px!important;
    }
  }
}
</style>
